import React, { useEffect, useMemo, useState } from 'react';
import { Edit, KeyRound, Plus, RefreshCw, Search, Shield, User, UserCheck, Users as UsersIcon } from 'lucide-react';
import { User as UserType } from '../../types';
import apiService from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import useEscapeToClose from '../../hooks/useEscapeToClose';

const STATUS_BADGE: Record<string, string> = {
  activo: 'bg-green-100 text-green-700 border-green-200',
  inactivo: 'bg-yellow-100 text-yellow-700 border-yellow-200'
};

const ROLE_BADGE: Record<string, string> = {
  admin: 'bg-blue-100 text-blue-700 border-blue-200',
  cliente: 'bg-gray-100 text-gray-700 border-gray-200'
};

const formatDateTime = (value?: string) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const StatusBadge: React.FC<{ status: 'activo' | 'inactivo' }> = ({ status }) => (
  <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full border ${STATUS_BADGE[status]}`}>
    {status === 'activo' ? 'Activo' : 'Inactivo'}
  </span>
);

const RoleBadge: React.FC<{ role: 'admin' | 'administrador' | 'superadmin' | 'cliente' }> = ({ role }) => (
  <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full border ${ROLE_BADGE[role]}`}>
    {role === 'superadmin' ? 'Superadministrador' : (role === 'admin' || role === 'administrador') ? 'Administrador' : 'Cliente'}
  </span>
);

const DeleteConfirm: React.FC<{ onConfirm: () => void; onCancel: () => void }> = ({ onConfirm, onCancel }) => (
  <div className="inline-flex items-center gap-2 text-xs">
    <span className="text-gray-600">¿Confirmar?</span>
    <button onClick={onConfirm} className="px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700">Sí</button>
    <button onClick={onCancel} className="px-2 py-1 rounded border border-gray-300 hover:bg-gray-50">No</button>
  </div>
);

const SkeletonRows: React.FC = () => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 animate-pulse">
      {[1, 2, 3, 4].map((i) => <div key={i} className="h-24 bg-white rounded-xl border border-gray-100 shadow-sm" />)}
    </div>
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 animate-pulse space-y-3">
      {[1, 2, 3, 4, 5].map((i) => <div key={i} className="h-12 bg-gray-100 rounded-lg" />)}
    </div>
  </div>
);

const EmptyState: React.FC<{ withFilters: boolean }> = ({ withFilters }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
    <UsersIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
    <p className="text-gray-800 font-medium">{withFilters ? 'No hay resultados para los filtros aplicados' : 'No hay usuarios cargados'}</p>
    <p className="text-sm text-gray-500 mt-1">{withFilters ? 'Probá con otros filtros de búsqueda.' : 'Los usuarios registrados aparecerán aquí.'}</p>
  </div>
);

const emptyCreateForm = {
  name: '', email: '', phone: '', company: '', password: '', role: 'admin' as 'admin' | 'superadmin', isActive: true,
  restrictClients: false, allowedClientIds: [] as number[],
  restrictSuppliers: false, allowedSupplierIds: [] as number[],
  isReadOnly: false,
};

const ScopePicker: React.FC<{
  label: string;
  helpText: string;
  items: Array<{ id: number; label: string }>;
  emptyText: string;
  restricted: boolean;
  selected: number[];
  onToggleRestricted: (value: boolean) => void;
  onChangeSelected: (ids: number[]) => void;
}> = ({ label, helpText, items, emptyText, restricted, selected, onToggleRestricted, onChangeSelected }) => {
  const toggleItem = (id: number) => {
    onChangeSelected(selected.includes(id) ? selected.filter((c) => c !== id) : [...selected, id]);
  };
  return (
    <div>
      <label className="mb-1 flex items-center gap-2 text-sm font-medium text-gray-700">
        <input type="checkbox" checked={restricted} onChange={(e) => onToggleRestricted(e.target.checked)} />
        {label}
      </label>
      <p className="mb-2 text-xs text-gray-500">{helpText}</p>
      {restricted && (
        <div className="max-h-40 overflow-y-auto rounded-lg border border-gray-300 p-2 space-y-1">
          {items.length === 0 && <p className="text-xs text-gray-500">{emptyText}</p>}
          {items.map((item) => (
            <label key={item.id} className="flex items-center gap-2 text-sm py-0.5">
              <input type="checkbox" checked={selected.includes(item.id)} onChange={() => toggleItem(item.id)} />
              {item.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const Users: React.FC = () => {
  const { user: currentUser } = useAuth();
  const isSuperadmin = currentUser?.role === 'superadmin';
  const [users, setUsers] = useState<UserType[]>([]);
  const [clients, setClients] = useState<UserType[]>([]);
  const [suppliers, setSuppliers] = useState<Array<{ id: number; name: string }>>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [createError, setCreateError] = useState<string | null>(null);
  const [isSavingCreate, setIsSavingCreate] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedUser, setSelectedUser] = useState<UserType | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [editingUser, setEditingUser] = useState<UserType | null>(null);
  const [passwordUser, setPasswordUser] = useState<UserType | null>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    role: 'cliente' as 'admin' | 'administrador' | 'superadmin' | 'cliente',
    isActive: true,
    restrictClients: false,
    allowedClientIds: [] as number[],
    restrictSuppliers: false,
    allowedSupplierIds: [] as number[],
    isReadOnly: false,
  });
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ newPassword: '', confirmPassword: '' });
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isSavingPassword, setIsSavingPassword] = useState(false);

  useBodyScrollLock(!!(selectedUser || showCreateModal || editingUser || passwordUser));
  useEscapeToClose(!!selectedUser, () => setSelectedUser(null));
  useEscapeToClose(showCreateModal, () => closeCreateModal());
  useEscapeToClose(!!editingUser, () => closeEditModal());
  useEscapeToClose(!!passwordUser, () => closePasswordModal());

  const fetchUsers = async (manualRetry = false) => {
    if (manualRetry) setIsRetrying(true);
    try {
      setLoadError(null);
      const [admins, administradores, superadmins] = await Promise.all([
        apiService.getUsers({ role: 'admin', page: 1, limit: 200 }),
        apiService.getUsers({ role: 'administrador', page: 1, limit: 200 }),
        apiService.getUsers({ role: 'superadmin', page: 1, limit: 200 }),
      ]);
      setUsers([...(admins.users || []), ...(administradores.users || []), ...(superadmins.users || [])]);
    } catch (error: any) {
      const detail = error?.response?.data?.detail || error?.response?.data?.message;
      setLoadError(detail || 'No se pudieron cargar los usuarios.');
    } finally {
      setIsLoading(false);
      setIsRetrying(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    apiService.getUsers({ role: 'cliente', page: 1, limit: 500 }).then((res) => setClients(res.users || [])).catch(() => setClients([]));
    apiService.getSuppliers().then((res) => setSuppliers(res.suppliers || [])).catch(() => setSuppliers([]));
  }, []);

  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      if (selectedStatus === 'active' && !u.isActive) return false;
      if (selectedStatus === 'inactive' && u.isActive) return false;
      if (!searchTerm) return true;
      const q = searchTerm.toLowerCase();
      return u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q) || (u.company || '').toLowerCase().includes(q);
    });
  }, [users, selectedStatus, searchTerm]);

  const inactiveCount = useMemo(() => users.filter((u) => !u.isActive).length, [users]);

  const handleOpenUser = (user: UserType) => {
    setSelectedUser(user);
  };

  const toggleStatus = async (id: number) => {
    try {
      setInlineError(null);
      const res = await apiService.toggleUserStatus(id);
      setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, isActive: res.user.isActive } : u)));
      setSelectedUser((prev) => (prev && prev.id === id ? { ...prev, isActive: res.user.isActive } : prev));
    } catch (error: any) {
      const detail = error?.response?.data?.detail || error?.response?.data?.message;
      setInlineError(detail || 'No se pudo actualizar el estado.');
    }
  };

  const handleEdit = (user: UserType) => {
    setEditingUser(user);
    const allowedClientIds = user.allowedClientIds || [];
    const allowedSupplierIds = user.allowedSupplierIds || [];
    setEditForm({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      company: user.company || '',
      role: user.role,
      isActive: user.isActive,
      restrictClients: allowedClientIds.length > 0,
      allowedClientIds,
      restrictSuppliers: allowedSupplierIds.length > 0,
      allowedSupplierIds,
      isReadOnly: !!user.isReadOnly,
    });
  };

  const deleteUser = async (user: UserType) => {
    try {
      setInlineError(null);
      await apiService.deleteUserByAdmin(user.id);
      setUsers((prev) => prev.filter((u) => u.id !== user.id));
      if (selectedUser?.id === user.id) setSelectedUser(null);
      setConfirmDeleteId(null);
    } catch (error: any) {
      const detail = error?.response?.data?.detail || error?.response?.data?.message;
      setInlineError(detail || 'No se pudo eliminar el usuario.');
    }
  };

  const openCreateModal = () => {
    setCreateForm(emptyCreateForm);
    setCreateError(null);
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setIsSavingCreate(false);
  };

  const submitCreate = async () => {
    if (createForm.name.trim().length < 2 || !createForm.email.includes('@')) {
      setCreateError('Nombre y email son obligatorios.');
      return;
    }
    if (createForm.password.trim().length < 6) {
      setCreateError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }
    try {
      setIsSavingCreate(true);
      setCreateError(null);
      const response = await apiService.createUserByAdmin({
        name: createForm.name.trim(),
        email: createForm.email.trim(),
        password: createForm.password,
        phone: createForm.phone.trim() || undefined,
        company: createForm.company.trim() || undefined,
        role: createForm.role,
        isActive: createForm.isActive,
        allowedClientIds: createForm.restrictClients ? createForm.allowedClientIds : null,
        allowedSupplierIds: createForm.restrictSuppliers ? createForm.allowedSupplierIds : null,
        isReadOnly: createForm.isReadOnly,
      });
      setUsers((prev) => [response.user, ...prev]);
      closeCreateModal();
    } catch (error: any) {
      const detail = error?.response?.data?.detail || error?.response?.data?.message;
      setCreateError(detail || 'No se pudo crear el usuario.');
    } finally {
      setIsSavingCreate(false);
    }
  };

  const closeEditModal = () => {
    setEditingUser(null);
    setIsSavingEdit(false);
  };

  const saveEditUser = async () => {
    if (!editingUser) return;
    try {
      setIsSavingEdit(true);
      setInlineError(null);
      const response = await apiService.updateUserByAdmin(editingUser.id, {
        name: editForm.name.trim(),
        email: editForm.email.trim(),
        phone: editForm.phone.trim() || undefined,
        company: editForm.company.trim() || undefined,
        role: editForm.role,
        isActive: editForm.isActive,
        allowedClientIds: editForm.restrictClients ? editForm.allowedClientIds : null,
        allowedSupplierIds: editForm.restrictSuppliers ? editForm.allowedSupplierIds : null,
        isReadOnly: editForm.isReadOnly,
      });
      setUsers((prev) => prev.map((u) => (u.id === editingUser.id ? { ...u, ...response.user } : u)));
      closeEditModal();
    } catch (error: any) {
      const detail = error?.response?.data?.detail || error?.response?.data?.message;
      setInlineError(detail || 'No se pudo guardar el usuario.');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const openPasswordModal = (user: UserType) => {
    setPasswordUser(user);
    setPasswordForm({ newPassword: '', confirmPassword: '' });
    setPasswordError(null);
  };

  const closePasswordModal = () => {
    setPasswordUser(null);
    setPasswordForm({ newPassword: '', confirmPassword: '' });
    setPasswordError(null);
    setIsSavingPassword(false);
  };

  const savePassword = async () => {
    if (!passwordUser) return;
    if (passwordForm.newPassword.trim().length < 6) {
      setPasswordError('La nueva contraseña debe tener al menos 6 caracteres.');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('Las contraseñas no coinciden.');
      return;
    }
    try {
      setIsSavingPassword(true);
      setPasswordError(null);
      setInlineError(null);
      await apiService.updateUserPasswordByAdmin(passwordUser.id, passwordForm.newPassword);
      closePasswordModal();
    } catch (error: any) {
      const detail = error?.response?.data?.detail || error?.response?.data?.message;
      setPasswordError(detail || 'No se pudo actualizar la contraseña.');
    } finally {
      setIsSavingPassword(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Gestión de Usuarios</h1>
          <p className="text-gray-600">Cargando información...</p>
        </div>
        <SkeletonRows />
      </div>
    );
  }

  const hasFilters = selectedStatus !== 'all' || searchTerm.trim().length > 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Gestión de Administradores</h1>
          <p className="text-gray-600">{users.length} registros en total · {inactiveCount} sin aprobar/inactivos</p>
        </div>
        <button onClick={openCreateModal} className="inline-flex w-full items-center justify-center gap-2 sm:w-auto sm:self-start rounded-lg bg-primary-600 px-4 py-2 text-sm text-white hover:bg-primary-700">
          <Plus className="h-4 w-4" />Nuevo usuario
        </button>
      </div>

      {loadError && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700 flex items-center justify-between gap-4">
          <span>{loadError}</span>
          <button
            onClick={() => fetchUsers(true)}
            disabled={isRetrying}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded border border-red-300 hover:bg-red-100 disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${isRetrying ? 'animate-spin' : ''}`} />
            Reintentar
          </button>
        </div>
      )}

      {inlineError && <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">{inlineError}</div>}

      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <div className="bg-white p-3 sm:p-5 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center"><User className="hidden sm:block h-6 w-6 text-blue-600" /><div className="sm:ml-3"><p className="text-xs sm:text-sm text-gray-600">Total</p><p className="text-xl sm:text-2xl font-bold">{users.length}</p></div></div>
        </div>
        <div className="bg-white p-3 sm:p-5 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center"><UserCheck className="hidden sm:block h-6 w-6 text-green-600" /><div className="sm:ml-3"><p className="text-xs sm:text-sm text-gray-600">Activos</p><p className="text-xl sm:text-2xl font-bold">{users.filter((u) => u.isActive).length}</p></div></div>
        </div>
        <div className="bg-white p-3 sm:p-5 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center"><Shield className="hidden sm:block h-6 w-6 text-blue-700" /><div className="sm:ml-3"><p className="text-xs sm:text-sm text-gray-600">Inactivos</p><p className="text-xl sm:text-2xl font-bold">{inactiveCount}</p></div></div>
        </div>
      </div>

      <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar administradores..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300"
          />
        </div>
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300"
        >
          <option value="all">Todos los estados</option>
          <option value="active">Activo</option>
          <option value="inactive">Inactivo</option>
        </select>
      </div>

      {filteredUsers.length === 0 ? (
        <EmptyState withFilters={hasFilters} />
      ) : (
        <>
        <div className="divide-y rounded-xl border border-gray-100 bg-white shadow-sm md:hidden">
          {filteredUsers.map((u) => (
            <div key={u.id} className={`space-y-2 p-4 ${!u.isActive ? 'border-l-2 border-l-red-400' : ''}`}>
              <button onClick={() => handleOpenUser(u)} className="block w-full space-y-1 text-left">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate font-medium text-gray-900">{u.name}</span>
                  <StatusBadge status={u.isActive ? 'activo' : 'inactivo'} />
                </div>
                <p className="truncate text-sm text-gray-500">{u.email}{u.company ? ` · ${u.company}` : ''}</p>
                <div className="flex flex-wrap items-center gap-1">
                  <RoleBadge role={u.role} />
                  {!!u.allowedClientIds?.length && <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full border bg-purple-100 text-purple-700 border-purple-200">{u.allowedClientIds.length} cliente{u.allowedClientIds.length === 1 ? '' : 's'}</span>}
                  {!!u.allowedSupplierIds?.length && <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full border bg-indigo-100 text-indigo-700 border-indigo-200">{u.allowedSupplierIds.length} proveedor{u.allowedSupplierIds.length === 1 ? '' : 'es'}</span>}
                  {u.isReadOnly && <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full border bg-gray-100 text-gray-700 border-gray-300">Solo lectura</span>}
                </div>
              </button>
              {confirmDeleteId === u.id ? (
                <DeleteConfirm onConfirm={() => deleteUser(u)} onCancel={() => setConfirmDeleteId(null)} />
              ) : (
                <div className="flex gap-2">
                  <button onClick={() => handleEdit(u)} className="inline-flex flex-1 items-center justify-center gap-1 rounded border border-amber-200 px-2 py-1.5 text-xs text-amber-700"><Edit className="h-3.5 w-3.5" />Editar</button>
                  <button onClick={() => openPasswordModal(u)} className="inline-flex flex-1 items-center justify-center gap-1 rounded border border-indigo-200 px-2 py-1.5 text-xs text-indigo-700"><KeyRound className="h-3.5 w-3.5" />Clave</button>
                  <button onClick={() => toggleStatus(u.id)} className="flex-1 rounded border border-gray-300 px-2 py-1.5 text-xs">{u.isActive ? 'Desactivar' : 'Activar'}</button>
                  <button onClick={() => setConfirmDeleteId(u.id)} className="flex-1 rounded border border-red-200 px-2 py-1.5 text-xs text-red-700">Eliminar</button>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="hidden md:block bg-white rounded-xl shadow-sm border border-gray-100 overflow-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Nombre</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Email</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Empresa</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Rol</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Estado</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Fecha alta</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => (
                <tr
                  key={u.id}
                  onClick={() => handleOpenUser(u)}
                  className={`border-t border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${!u.isActive ? 'border-l-2 border-l-red-400' : ''}`}
                >
                  <td className="px-4 py-3">{u.name}</td>
                  <td className="px-4 py-3">{u.email}</td>
                  <td className="px-4 py-3">{u.company || '-'}</td>
                  <td className="px-4 py-3">
                    <RoleBadge role={u.role} />
                    {!!u.allowedClientIds?.length && (
                      <span className="ml-1 inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full border bg-purple-100 text-purple-700 border-purple-200" title="Solo ve/carga los clientes asignados">
                        {u.allowedClientIds.length} cliente{u.allowedClientIds.length === 1 ? '' : 's'}
                      </span>
                    )}
                    {!!u.allowedSupplierIds?.length && (
                      <span className="ml-1 inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full border bg-indigo-100 text-indigo-700 border-indigo-200" title="Solo ve/carga los proveedores asignados">
                        {u.allowedSupplierIds.length} proveedor{u.allowedSupplierIds.length === 1 ? '' : 'es'}
                      </span>
                    )}
                    {u.isReadOnly && (
                      <span className="ml-1 inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full border bg-gray-100 text-gray-700 border-gray-300" title="No puede cargar, editar ni eliminar nada">
                        Solo lectura
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={u.isActive ? 'activo' : 'inactivo'} /></td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatDateTime(u.createdAt)}</td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    {confirmDeleteId === u.id ? (
                      <DeleteConfirm onConfirm={() => deleteUser(u)} onCancel={() => setConfirmDeleteId(null)} />
                    ) : (
                      <div className="flex items-center gap-2">
                        <button onClick={() => handleEdit(u)} className="p-1.5 text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded" title="Editar">
                          <Edit className="h-4 w-4" />
                        </button>
                        <button onClick={() => openPasswordModal(u)} className="p-1.5 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded" title="Cambiar contraseña">
                          <KeyRound className="h-4 w-4" />
                        </button>
                        <button onClick={() => toggleStatus(u.id)} className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50">
                          {u.isActive ? 'Desactivar' : 'Activar'}
                        </button>
                        <button onClick={() => setConfirmDeleteId(u.id)} className="px-2 py-1 text-xs rounded border border-red-200 text-red-700 hover:bg-red-50">
                          Eliminar
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      {selectedUser && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 sm:p-4">
          <div className="w-full max-w-3xl rounded-t-xl sm:rounded-xl bg-white shadow-xl max-h-[92vh] sm:max-h-[90vh] flex flex-col overflow-hidden">
            <div className="border-b border-gray-100 px-4 sm:px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Detalle de usuario</h2>
              <button onClick={() => setSelectedUser(null)} className="text-gray-500 hover:text-gray-700 text-xl leading-none">×</button>
            </div>
            <div className="p-4 sm:p-6 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Nombre</p>
                  <p className="font-medium text-gray-900">{selectedUser.name}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Email</p>
                  <a href={`mailto:${selectedUser.email}`} className="font-medium text-primary-700 hover:underline">{selectedUser.email}</a>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Teléfono</p>
                  <p className="font-medium text-gray-900">{selectedUser.phone || '-'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Empresa</p>
                  <p className="font-medium text-gray-900">{selectedUser.company || '-'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Rol</p>
                  <div className="mt-1"><RoleBadge role={selectedUser.role} /></div>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Estado</p>
                  <div className="mt-1"><StatusBadge status={selectedUser.isActive ? 'activo' : 'inactivo'} /></div>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Fecha de alta</p>
                  <p className="font-medium text-gray-900">{formatDateTime(selectedUser.createdAt)}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Último login</p>
                  <p className="font-medium text-gray-900">{formatDateTime(selectedUser.lastLogin)}</p>
                </div>
              </div>
            </div>
            <div className="flex flex-col-reverse gap-3 border-t border-gray-100 px-4 sm:px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2">
                {confirmDeleteId === selectedUser.id ? (
                  <DeleteConfirm onConfirm={() => deleteUser(selectedUser)} onCancel={() => setConfirmDeleteId(null)} />
                ) : (
                  <button onClick={() => setConfirmDeleteId(selectedUser.id)} className="px-3 py-2 rounded-lg border border-red-200 text-red-700 hover:bg-red-50">
                    Eliminar
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center">
                <button onClick={() => setSelectedUser(null)} className="hidden sm:block px-4 py-2 border rounded-lg hover:bg-gray-50">Cerrar</button>
                <button onClick={() => openPasswordModal(selectedUser)} className="px-4 py-2 border border-indigo-200 text-indigo-700 rounded-lg hover:bg-indigo-50">Cambiar clave</button>
                <button onClick={() => handleEdit(selectedUser)} className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">Editar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 sm:p-4">
          <div className="w-full max-w-2xl rounded-t-xl sm:rounded-xl bg-white shadow-xl max-h-[92vh] sm:max-h-[90vh] flex flex-col overflow-hidden">
            <div className="border-b border-gray-100 px-4 sm:px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Nuevo usuario</h2>
              <button onClick={closeCreateModal} className="text-gray-500 hover:text-gray-700 text-xl leading-none">×</button>
            </div>
            <div className="grid grid-cols-1 gap-4 px-4 sm:px-6 py-5 overflow-y-auto">
              {createError && <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">{createError}</div>}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Nombre *</label>
                <input value={createForm.name} onChange={(e) => setCreateForm((prev) => ({ ...prev, name: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Email *</label>
                <input type="email" value={createForm.email} onChange={(e) => setCreateForm((prev) => ({ ...prev, email: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Contraseña *</label>
                <input type="password" value={createForm.password} onChange={(e) => setCreateForm((prev) => ({ ...prev, password: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300" placeholder="Mínimo 6 caracteres" />
                <p className="mt-1 text-xs text-gray-500">Se la pasás vos a la persona; después la puede cambiar desde "Cambiar contraseña".</p>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Teléfono</label>
                  <input value={createForm.phone} onChange={(e) => setCreateForm((prev) => ({ ...prev, phone: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Empresa</label>
                  <input value={createForm.company} onChange={(e) => setCreateForm((prev) => ({ ...prev, company: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300" />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Rol</label>
                  <select value={createForm.role} onChange={(e) => setCreateForm((prev) => ({ ...prev, role: e.target.value as 'admin' | 'superadmin' }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300">
                    <option value="admin">Administrador</option>
                    {isSuperadmin && <option value="superadmin">Superadministrador</option>}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Estado</label>
                  <select value={createForm.isActive ? 'active' : 'inactive'} onChange={(e) => setCreateForm((prev) => ({ ...prev, isActive: e.target.value === 'active' }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300">
                    <option value="active">Activo</option>
                    <option value="inactive">Inactivo</option>
                  </select>
                </div>
              </div>
              {createForm.role === 'admin' && (
                <>
                  <ScopePicker
                    label="Restringir a clientes específicos"
                    helpText="Sin marcar, esta cuenta ve todos los clientes (como hoy). Marcada, solo ve/carga los clientes que elijas."
                    items={clients.map((c) => ({ id: c.id, label: c.company || c.name }))}
                    emptyText="No hay clientes cargados todavía."
                    restricted={createForm.restrictClients}
                    selected={createForm.allowedClientIds}
                    onToggleRestricted={(value) => setCreateForm((prev) => ({ ...prev, restrictClients: value }))}
                    onChangeSelected={(ids) => setCreateForm((prev) => ({ ...prev, allowedClientIds: ids }))}
                  />
                  <ScopePicker
                    label="Restringir a proveedores específicos"
                    helpText="Sin marcar, esta cuenta ve todos los proveedores. Marcada, solo ve/carga los proveedores que elijas."
                    items={suppliers.map((s) => ({ id: s.id, label: s.name }))}
                    emptyText="No hay proveedores cargados todavía."
                    restricted={createForm.restrictSuppliers}
                    selected={createForm.allowedSupplierIds}
                    onToggleRestricted={(value) => setCreateForm((prev) => ({ ...prev, restrictSuppliers: value }))}
                    onChangeSelected={(ids) => setCreateForm((prev) => ({ ...prev, allowedSupplierIds: ids }))}
                  />
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <input type="checkbox" checked={createForm.isReadOnly} onChange={(e) => setCreateForm((prev) => ({ ...prev, isReadOnly: e.target.checked }))} />
                    Solo lectura (no puede cargar, editar ni eliminar nada)
                  </label>
                </>
              )}
            </div>
            <div className="flex items-center justify-end border-t border-gray-100 px-4 sm:px-6 py-4 gap-3 [&>button]:flex-1 sm:[&>button]:flex-none">
              <button onClick={closeCreateModal} className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50">Cancelar</button>
              <button onClick={submitCreate} disabled={isSavingCreate} className="rounded-lg bg-primary-600 px-4 py-2 text-sm text-white disabled:opacity-50">
                {isSavingCreate ? 'Creando...' : 'Crear usuario'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editingUser && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 sm:p-4">
          <div className="w-full max-w-2xl rounded-t-xl sm:rounded-xl bg-white shadow-xl max-h-[92vh] sm:max-h-[90vh] flex flex-col overflow-hidden">
            <div className="border-b border-gray-100 px-4 sm:px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Editar usuario</h2>
              <button onClick={closeEditModal} className="text-gray-500 hover:text-gray-700 text-xl leading-none">×</button>
            </div>
            <div className="grid grid-cols-1 gap-4 px-4 sm:px-6 py-5 overflow-y-auto">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Nombre</label>
                <input value={editForm.name} onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Email</label>
                <input type="email" value={editForm.email} onChange={(e) => setEditForm((prev) => ({ ...prev, email: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300" />
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Teléfono</label>
                  <input value={editForm.phone} onChange={(e) => setEditForm((prev) => ({ ...prev, phone: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Empresa</label>
                  <input value={editForm.company} onChange={(e) => setEditForm((prev) => ({ ...prev, company: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300" />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Rol</label>
                  <select value={editForm.role} onChange={(e) => setEditForm((prev) => ({ ...prev, role: e.target.value as 'admin' | 'administrador' | 'superadmin' | 'cliente' }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300">
                    <option value="cliente">Cliente</option>
                    <option value="admin">Administrador</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Estado</label>
                  <select value={editForm.isActive ? 'active' : 'inactive'} onChange={(e) => setEditForm((prev) => ({ ...prev, isActive: e.target.value === 'active' }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300">
                    <option value="active">Activo</option>
                    <option value="inactive">Inactivo</option>
                  </select>
                </div>
              </div>
              {(editForm.role === 'admin' || editForm.role === 'administrador') && (
                <>
                  <ScopePicker
                    label="Restringir a clientes específicos"
                    helpText="Sin marcar, esta cuenta ve todos los clientes (como hoy). Marcada, solo ve/carga los clientes que elijas."
                    items={clients.map((c) => ({ id: c.id, label: c.company || c.name }))}
                    emptyText="No hay clientes cargados todavía."
                    restricted={editForm.restrictClients}
                    selected={editForm.allowedClientIds}
                    onToggleRestricted={(value) => setEditForm((prev) => ({ ...prev, restrictClients: value }))}
                    onChangeSelected={(ids) => setEditForm((prev) => ({ ...prev, allowedClientIds: ids }))}
                  />
                  <ScopePicker
                    label="Restringir a proveedores específicos"
                    helpText="Sin marcar, esta cuenta ve todos los proveedores. Marcada, solo ve/carga los proveedores que elijas."
                    items={suppliers.map((s) => ({ id: s.id, label: s.name }))}
                    emptyText="No hay proveedores cargados todavía."
                    restricted={editForm.restrictSuppliers}
                    selected={editForm.allowedSupplierIds}
                    onToggleRestricted={(value) => setEditForm((prev) => ({ ...prev, restrictSuppliers: value }))}
                    onChangeSelected={(ids) => setEditForm((prev) => ({ ...prev, allowedSupplierIds: ids }))}
                  />
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <input type="checkbox" checked={editForm.isReadOnly} onChange={(e) => setEditForm((prev) => ({ ...prev, isReadOnly: e.target.checked }))} />
                    Solo lectura (no puede cargar, editar ni eliminar nada)
                  </label>
                </>
              )}
            </div>
            <div className="flex items-center justify-end border-t border-gray-100 px-4 sm:px-6 py-4">
              <div className="flex w-full sm:w-auto gap-3 [&>button]:flex-1 sm:[&>button]:flex-none">
                <button onClick={closeEditModal} className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50">Cerrar</button>
                <button onClick={saveEditUser} disabled={isSavingEdit} className="rounded-lg bg-primary-600 px-4 py-2 text-sm text-white disabled:opacity-50">
                  {isSavingEdit ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {passwordUser && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 sm:p-4">
          <div className="w-full max-w-lg rounded-t-xl sm:rounded-xl bg-white shadow-xl max-h-[92vh] sm:max-h-[90vh] flex flex-col overflow-hidden">
            <div className="border-b border-gray-100 px-4 sm:px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Cambiar contraseña</h2>
              <button onClick={closePasswordModal} className="text-gray-500 hover:text-gray-700 text-xl leading-none">×</button>
            </div>
            <div className="px-4 sm:px-6 py-5 space-y-4 overflow-y-auto">
              <div className="text-sm text-gray-600">
                Usuario: <span className="font-medium text-gray-900">{passwordUser.email}</span>
              </div>
              {passwordError && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">{passwordError}</div>
              )}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Nueva contraseña</label>
                <input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm((prev) => ({ ...prev, newPassword: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Confirmar contraseña</label>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
              </div>
            </div>
            <div className="flex items-center justify-end border-t border-gray-100 px-4 sm:px-6 py-4 gap-3 [&>button]:flex-1 sm:[&>button]:flex-none">
              <button onClick={closePasswordModal} className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50">Cancelar</button>
              <button onClick={savePassword} disabled={isSavingPassword} className="rounded-lg bg-primary-600 px-4 py-2 text-sm text-white disabled:opacity-50">
                {isSavingPassword ? 'Guardando...' : 'Guardar contraseña'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
